import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { GradeFinder } from "@/components/GradeFinder";

export const LANGS = ["en", "id", "zh"];

// Maps old single-page anchors (and legacy paths) to the new routes.
export const LEGACY_ANCHOR_MAP = {
  home: "",
  why: "/about",
  about: "/about",
  products: "/grades",
  grades: "/grades",
  applications: "/applications",
  quality: "/quality",
  contact: "/contact",
};

export const detectLang = () => {
  const stored = localStorage.getItem("clara_lang");
  if (LANGS.includes(stored)) return stored;
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("id")) return "id";
  if (nav.startsWith("zh")) return "zh";
  return "en";
};

// Scrolls to top on every route change (Lenis-aware).
export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// "/" — honour old anchor links (#products → /grades) and detect language.
export const RootRedirect = () => {
  const { hash } = useLocation();
  const target = LEGACY_ANCHOR_MAP[hash.replace("#", "")] ?? "";
  return <Navigate to={`/${detectLang()}${target}`} replace />;
};

// Any unmatched non-language path (e.g. /products, /about/anything).
export const LegacyRedirect = () => {
  const { pathname } = useLocation();
  const first = pathname.split("/").filter(Boolean)[0] || "";
  const target = LEGACY_ANCHOR_MAP[first] ?? "";
  return <Navigate to={`/${detectLang()}${target}`} replace />;
};

// Wraps every /:lang/* route: validates the language, syncs i18n, renders chrome.
export const LangLayout = () => {
  const { lang } = useParams();
  const { setLang } = useLang();
  const valid = LANGS.includes(lang);

  useEffect(() => {
    if (valid) setLang(lang);
  }, [lang, valid, setLang]);

  if (!valid) {
    return <Navigate to={`/${detectLang()}${LEGACY_ANCHOR_MAP[lang] ?? ""}`} replace />;
  }

  return (
    <div className="App bg-clara-cream">
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <GradeFinder />
    </div>
  );
};

export const LangHomeRedirect = () => {
  const { lang } = useParams();
  return <Navigate to={`/${LANGS.includes(lang) ? lang : detectLang()}`} replace />;
};
